import { useEffect, useRef, useState } from "react";
import type {
  ClientMessage,
  DisplayCommand,
  HostAction,
  HostSurface,
  Role,
  ServerMessage,
  Snapshot,
} from "../../shared/types";

export interface GameApi {
  snapshot: Snapshot | null;
  connected: boolean;
  quizTitle: string;
  hostName: string;
  error: string | null;
  /** Per-player secret confirmed by the server — persist it to resume this name. */
  playerToken: string | null;
  join(nickname: string, emoji?: string, token?: string): void;
  sendHost(action: HostAction): void;
  sendAnswer(ids: string[]): void;
}

export interface GameOptions {
  /** Host sockets only: the controlling phone ("control", default) or big-screen ("display"). */
  surface?: HostSurface;
  /** Receives transient display commands (fullscreen toggle) — for the big screen. */
  onCommand?: (cmd: DisplayCommand) => void;
}

/**
 * Connects to the Room Durable Object over a websocket and tracks the live
 * snapshot. Auto-reconnects with backoff; on reconnect a player re-sends its
 * join so it resumes its score (nickname is the identity key in the room).
 * The host passes its session token so the DO accepts its control messages.
 */
export function useGame(pin: string | null, role: Role, token?: string | null, opts?: GameOptions): GameApi {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const [quizTitle, setQuizTitle] = useState("");
  const [hostName, setHostName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [playerToken, setPlayerToken] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const nickRef = useRef<string | null>(null);
  const emojiRef = useRef<string | undefined>(undefined);
  // Per-player secret: presented on rejoin so the room re-binds us to our name.
  const tokenRef = useRef<string | undefined>(undefined);
  const closedRef = useRef(false);
  // Keep the latest command handler without forcing a reconnect when it changes.
  const onCommandRef = useRef(opts?.onCommand);
  onCommandRef.current = opts?.onCommand;
  const surface = opts?.surface;

  useEffect(() => {
    if (!pin) return;
    closedRef.current = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const open = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const tokenQs = role === "host" && token ? `&token=${encodeURIComponent(token)}` : "";
      const surfaceQs = role === "host" && surface ? `&surface=${surface}` : "";
      const ws = new WebSocket(`${proto}://${location.host}/ws/${pin}?role=${role}${tokenQs}${surfaceQs}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        retry = 0;
        if (nickRef.current)
          ws.send(
            JSON.stringify({
              t: "join",
              nickname: nickRef.current,
              emoji: emojiRef.current,
              token: tokenRef.current,
            } satisfies ClientMessage),
          );
      };
      ws.onmessage = (e) => {
        let m: ServerMessage;
        try {
          m = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (m.t === "state") {
          setSnapshot(m.snapshot);
          setError(null);
        } else if (m.t === "answer-accepted") {
          setSnapshot((current) => {
            if (current?.phase !== "question" || current.question?.id !== m.questionId || !current.you) return current;
            return { ...current, you: { ...current.you, answered: true } };
          });
        } else if (m.t === "answer-progress") {
          setSnapshot((current) => {
            if (current?.phase !== "question" || current.question?.id !== m.questionId) return current;
            return {
              ...current,
              question: { ...current.question, answeredCount: m.answeredCount },
            };
          });
        } else if (m.t === "welcome") {
          setQuizTitle(m.quizTitle);
          if (m.hostName) setHostName(m.hostName);
        } else if (m.t === "joined") {
          tokenRef.current = m.token;
          setPlayerToken(m.token);
        } else if (m.t === "cmd") {
          onCommandRef.current?.(m.cmd);
        } else if (m.t === "error") setError(m.message);
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closedRef.current) {
          retry++;
          timer = setTimeout(open, Math.min(3000, 300 * retry));
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      };
    };

    open();
    return () => {
      closedRef.current = true;
      if (timer) clearTimeout(timer);
      wsRef.current?.close();
    };
  }, [pin, role, token, surface]);

  const send = (m: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
  };

  return {
    snapshot,
    connected,
    quizTitle,
    hostName,
    error,
    playerToken,
    join(nickname: string, emoji?: string, token?: string) {
      nickRef.current = nickname;
      emojiRef.current = emoji;
      // A resumed session passes its stored token; keep any token already confirmed.
      if (token) tokenRef.current = token;
      send({ t: "join", nickname, emoji, token: tokenRef.current });
    },
    sendHost(action) {
      send({ t: "host", action });
    },
    sendAnswer(ids) {
      send({ t: "answer", answerIds: ids });
    },
  };
}
